import { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';
import { sleep } from '../../../../utils/generics';
import { getNameWithTeamId } from '../../utils/getPersisterKeyWithTeamId.util';
import { OfflineDataEncryption } from '../../utils/offlineDataEncryption/OfflineDataEncryption.util';
import { waitOfflineEncryptionKey } from '../../utils/waitOfflineEncryptionKey.util';

const name = 'offline-react-query';

export const offlineQueryClientIDBPersister: Persister = {
  removeClient: async () => {
    const nameWithTeamId = getNameWithTeamId(name);
    if (!nameWithTeamId) {
      return;
    }

    await del(nameWithTeamId);
  },
  persistClient: async (client: PersistedClient) => {
    const nameWithTeamId = getNameWithTeamId(name);
    if (!nameWithTeamId) {
      return;
    }

    const hasData = client.clientState.queries.length > 0;

    if (hasData) {
      await waitOfflineEncryptionKey();

      const offlineDataEncryption = new OfflineDataEncryption();

      const clientWithEncryptedData = {
        ...client,
        clientState: {
          ...client.clientState,
          queries: client.clientState.queries.map((query) => {
            const encryptedData = offlineDataEncryption.encryptOfflineData({
              input: query.state.data,
            });

            return {
              ...query,
              state: {
                ...query.state,
                data: encryptedData,
              },
            };
          }),
        },
      };

      await set(nameWithTeamId, clientWithEncryptedData);
    } else {
      await sleep(1);
      await del(nameWithTeamId);
    }
  },
  restoreClient: async () => {
    const nameWithTeamId = getNameWithTeamId(name);
    if (!nameWithTeamId) {
      return;
    }

    const persistedClient = await get<PersistedClient>(nameWithTeamId);

    if (!persistedClient) {
      return;
    }

    try {
      persistedClient.clientState.queries.length && (await waitOfflineEncryptionKey());

      const offlineDataEncryption = new OfflineDataEncryption();

      const persistedClientWithDecryptedData = {
        ...persistedClient,
        clientState: {
          ...persistedClient.clientState,
          queries: persistedClient.clientState.queries.map((query) => ({
            ...query,
            state: {
              ...query.state,
              data: offlineDataEncryption.decryptOfflineData({ input: query.state.data }),
            },
          })),
        },
      };

      return persistedClientWithDecryptedData;
    } catch (error) {
      console.error('Error decrypting offline data:', error);
      return undefined;
    }
  },
};
